import React, { useContext, useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, gql } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { GET_NOTES_QUERY } from '../query/gqlQueries.js'
import styles from './NotesListDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import TaskIcon from '@mui/icons-material/Task'

const DELETE_NOTE_MUTATION = gql`
  mutation DeleteNote($identity: ID!) {
    deleteNote(identity: $identity)
  }
`

function formatDate(dateStr) {
  if (!dateStr) return ''
  try {
    // Neo4j DateTime comes back as an object or ISO string
    const d = new Date(typeof dateStr === 'object' ? dateStr.toString() : dateStr)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

function NoteCard({ note, onOpen, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const props = note.properties || {}

  // Parse mapData to get a quick node count for the preview badge
  let nodeCount = 0
  let strokeCount = 0
  try {
    const parsed = JSON.parse(props.mapData || '{}')
    nodeCount = (parsed.nodes || []).length
    strokeCount = (parsed.strokes || []).length
  } catch {}

  return (
    <div className={styles.card}>
      <div className={styles.cardBody} onClick={() => onOpen(note)}>
        <div className={styles.cardTitle}>{props.title || 'Untitled'}</div>

        <div className={styles.cardMeta}>
          {props.linkedProject && (
            <span className={styles.metaChip}>
              <FolderOpenIcon style={{ fontSize: 12 }} />
              {props.linkedProject}
            </span>
          )}
          {props.linkedTaskId && (
            <span className={styles.metaChip}>
              <TaskIcon style={{ fontSize: 12 }} />
              Task linked
            </span>
          )}
        </div>

        <div className={styles.cardStats}>
          <span className={styles.statBadge}>{nodeCount} node{nodeCount !== 1 ? 's' : ''}</span>
          {strokeCount > 0 && (
            <span className={styles.statBadge}>{strokeCount} drawing{strokeCount !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.dateLabel}>{formatDate(props.dateAdded)}</span>
        <div className={styles.cardActions}>
          <button className={styles.editBtn} onClick={() => onOpen(note)} title="Open note">
            <EditNoteIcon fontSize="small" />
          </button>
          {confirmDelete ? (
            <>
              <button className={styles.confirmBtn} onClick={() => onDelete(note.identity)}>Delete</button>
              <button className={styles.cancelBtn} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          ) : (
            <button className={styles.deleteBtn} onClick={() => setConfirmDelete(true)} title="Delete note">
              <DeleteOutlineIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function NotesListDialog() {
  const { dialogStates, openDialog, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const [filterProject, setFilterProject] = useState('')

  const isOpen = dialogStates['dialogNotesList']

  const { data, loading, error, refetch } = useQuery(GET_NOTES_QUERY, {
    variables: { username },
    skip: !username || !isOpen,
    fetchPolicy: 'network-only',
  })

  const [deleteNote] = useMutation(DELETE_NOTE_MUTATION, {
    onCompleted: () => refetch(),
  })

  if (!isOpen) return null

  const notes = data?.getNotes || []

  // Unique projects for filter
  const projects = [...new Set(notes.map((n) => n.properties?.linkedProject).filter(Boolean))]

  const filtered = filterProject
    ? notes.filter((n) => n.properties?.linkedProject === filterProject)
    : notes

  function handleOpen(note) {
    // Pass note data through to the editor via a custom event
    // (avoids prop-drilling through Hud.js)
    window.dispatchEvent(new CustomEvent('openNote', { detail: note }))
    closeDialog('dialogNotesList')
    openDialog('dialogAddNote')
  }

  async function handleDelete(identity) {
    await deleteNote({ variables: { identity } })
  }

  function handleClose() {
    closeDialog('dialogNotesList')
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Notes</span>
            <h2 className={styles.headerTitle}>My Notes</h2>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.newBtn}
              onClick={() => { closeDialog('dialogNotesList'); openDialog('dialogAddNote') }}>
              <NoteAddIcon fontSize="small" />
              New Note
            </button>
            <button className={styles.closeBtn} onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {projects.length > 0 && (
          <div className={styles.filterBar}>
            <button
              className={`${styles.filterChip} ${filterProject === '' ? styles.filterChipActive : ''}`}
              onClick={() => setFilterProject('')}>
              All
            </button>
            {projects.map((p) => (
              <button
                key={p}
                className={`${styles.filterChip} ${filterProject === p ? styles.filterChipActive : ''}`}
                onClick={() => setFilterProject(p)}>
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          {loading && <div className={styles.empty}>Loading notes…</div>}
          {error && <div className={styles.empty}>Error loading notes.</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className={styles.empty}>
              <span>No notes yet.</span>
              <button
                className={styles.emptyAction}
                onClick={() => { closeDialog('dialogNotesList'); openDialog('dialogAddNote') }}>
                Create your first note →
              </button>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className={styles.grid}>
              {filtered.map((note) => (
                <NoteCard
                  key={note.identity}
                  note={note}
                  onOpen={handleOpen}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default NotesListDialog
